import type { Skeleton } from '../skeleton/core';
import {
  isClavicleLikeName,
  isHeadLikeName,
  isInlineChestLikeName,
  isNeckLikeName,
  isSpineLikeName,
} from '../schema/mapping';

// ═══════════════════════════════════════════════════════════════════════
// CHAIN DEFINITION (lightweight preset-only version)
// ═══════════════════════════════════════════════════════════════════════

export interface PresetChainDef {
  name: string;
  startBone: string;
  endBone: string;
  ikGoal?: string | null;
}

function C(name: string, startBone: string, endBone: string, ikGoal?: string): PresetChainDef {
  return { name, startBone, endBone, ikGoal: ikGoal ?? null };
}

// ═══════════════════════════════════════════════════════════════════════
// PREFIX / SUFFIX DETECTION
// ═══════════════════════════════════════════════════════════════════════

export type SuffixPattern = [string, number]; // [separator, digitLength]

const COMMON_PREFIX_PATTERNS = [
  'mixamorig:', 'mixamorig_', 'Mixamorig:', 'Mixamorig_',
  'mixamo:', 'mixamo_',
  'Bip001 ', 'Bip01 ', 'Bip001_', 'Bip01_',
  'Character1_', 'Character_',
  'Armature|', 'Armature_',
  'Skeleton_', 'SK_',
  'Root|',
];

function detectCommonPrefix(boneNames: string[], minBonesRatio = 0.8): string | null {
  if (boneNames.length < 3) return null;

  for (const pattern of COMMON_PREFIX_PATTERNS) {
    const patternLower = pattern.toLowerCase();
    let matches = 0;
    for (const name of boneNames) {
      if (name.toLowerCase().startsWith(patternLower)) matches++;
    }
    if (matches >= boneNames.length * minBonesRatio) {
      for (const name of boneNames) {
        if (name.toLowerCase().startsWith(patternLower)) {
          return name.slice(0, pattern.length);
        }
      }
      return pattern;
    }
  }

  if (boneNames.length < 5) return null;

  const prefixCandidates: Record<string, number> = {};
  for (const name of boneNames) {
    for (const sep of ['_', ':', '|', ' ']) {
      const idx = name.indexOf(sep);
      if (idx >= 0) {
        const prefix = name.slice(0, idx + 1);
        if (prefix.length >= 2) {
          const key = prefix.toLowerCase();
          prefixCandidates[key] = (prefixCandidates[key] ?? 0) + 1;
        }
      }
    }
  }

  const sorted = Object.entries(prefixCandidates).sort((a, b) => b[1] - a[1]);
  for (const [prefixLower, count] of sorted) {
    if (count >= boneNames.length * minBonesRatio) {
      for (const name of boneNames) {
        if (name.toLowerCase().startsWith(prefixLower)) {
          return name.slice(0, prefixLower.length);
        }
      }
    }
  }

  return null;
}

function stripPrefix(name: string, prefix: string): string {
  if (name.toLowerCase().startsWith(prefix.toLowerCase())) {
    return name.slice(prefix.length);
  }
  return name;
}

function detectCommonSuffix(
  boneNames: string[],
  minBonesRatio = 0.7,
  minCount = 8,
): SuffixPattern | null {
  if (boneNames.length === 0) return null;

  const total = boneNames.length;
  const patternCounts = new Map<string, number>();

  const suffixRe = /([_:|\s])(\d{1,4})$/;
  for (const name of boneNames) {
    const m = suffixRe.exec(name);
    if (!m) continue;
    const key = `${m[1]}|${m[2].length}`;
    patternCounts.set(key, (patternCounts.get(key) ?? 0) + 1);
  }

  if (patternCounts.size === 0) return null;

  let bestKey = '';
  let bestCount = 0;
  for (const [key, count] of patternCounts) {
    if (count > bestCount) {
      bestCount = count;
      bestKey = key;
    }
  }

  if (bestCount < minCount || bestCount < total * minBonesRatio) return null;

  const [sep, digitLenStr] = bestKey.split('|');
  return [sep, parseInt(digitLenStr, 10)];
}

function stripSuffixPattern(name: string, suffixPattern: SuffixPattern): string {
  const [sep, digitLen] = suffixPattern;
  if (digitLen <= 0) return name;
  const pattern = new RegExp(sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(\\d{1,4})$');
  const m = pattern.exec(name);
  if (m) {
    return name.slice(0, -(m[1].length + sep.length));
  }
  return name;
}

function stripTerminalNumberToken(name: string): string {
  return name.replace(/(?:[_:|\s-])\d{1,4}$/u, '');
}

function stripLeadingMatchNoise(name: string): string {
  return name.replace(/^[_:|\s.-]+/u, '');
}

function isFingerChainName(chainName: string): boolean {
  return /(?:Thumb|Index|Middle|Ring|Pinky)$/u.test(chainName);
}

function collectEffectiveChildren(skeleton: Skeleton, boneIdx: number): number[] {
  const result: number[] = [];

  function visit(idx: number): void {
    const bone = skeleton.bone(idx);
    if (bone.auxiliary) {
      for (const childIdx of bone.children) visit(childIdx);
      return;
    }
    result.push(idx);
  }

  for (const childIdx of skeleton.bone(boneIdx).children) {
    visit(childIdx);
  }

  return result;
}

function inferFingerChainEnd(
  skeleton: Skeleton,
  startIdx: number,
  maxBones = 3,
): number | null {
  let current = startIdx;
  let boneCount = 1;

  while (boneCount < maxBones) {
    const effectiveChildren = collectEffectiveChildren(skeleton, current);
    if (effectiveChildren.length !== 1) break;
    current = effectiveChildren[0];
    boneCount += 1;
  }

  return boneCount >= 2 ? current : null;
}

function inferSpineChainEnd(
  skeleton: Skeleton,
  startIdx: number,
): number | null {
  let current = startIdx;
  let best: number | null = null;

  while (true) {
    const spineChildren = collectEffectiveChildren(skeleton, current).filter((childIdx) => {
      const child = skeleton.bone(childIdx);
      const childName = child.name.toLowerCase();
      if (child.auxiliary || child.hasSide) return false;
      if (isNeckLikeName(childName) || isHeadLikeName(childName)) return false;
      if (isClavicleLikeName(childName)) return false;
      return isSpineLikeName(childName) || isInlineChestLikeName(childName);
    });

    if (spineChildren.length !== 1) break;
    current = spineChildren[0];
    best = current;
  }

  return best;
}

function inferPresetChainEnd(
  skeleton: Skeleton,
  chainName: string,
  startIdx: number,
): number | null {
  if (chainName === 'Spine') {
    return inferSpineChainEnd(skeleton, startIdx);
  }

  return null;
}

// ═══════════════════════════════════════════════════════════════════════
// RIG PRESET CLASS
// ═══════════════════════════════════════════════════════════════════════

export class RigPreset {
  name: string;
  id: string;
  signatureBones: Set<string>;
  chains: PresetChainDef[];
  optionalBones: Set<string>;
  confidenceBoost: number;
  description: string;
  caseSensitive: boolean;

  constructor(fields: {
    name: string;
    id: string;
    signatureBones: string[];
    chains: PresetChainDef[];
    optionalBones?: string[];
    confidenceBoost?: number;
    description?: string;
    caseSensitive?: boolean;
  }) {
    this.name = fields.name;
    this.id = fields.id;
    this.signatureBones = new Set(fields.signatureBones);
    this.chains = fields.chains;
    this.optionalBones = new Set(fields.optionalBones ?? []);
    this.confidenceBoost = fields.confidenceBoost ?? 0.15;
    this.description = fields.description ?? '';
    this.caseSensitive = fields.caseSensitive ?? false;
  }

  private normalizeName(name: string): string {
    return this.caseSensitive ? name : name.toLowerCase();
  }

  private normalizeForMatch(
    name: string,
    prefix: string | null,
    suffix: SuffixPattern | null,
  ): string {
    let result = name;
    if (prefix && result.toLowerCase().startsWith(prefix.toLowerCase())) {
      result = result.slice(prefix.length);
    }
    if (suffix) {
      result = stripSuffixPattern(result, suffix);
    }
    return this.normalizeName(result);
  }

  private getBoneNames(
    skeleton: Skeleton,
    prefix: string | null,
    suffix: SuffixPattern | null,
  ): Set<string> {
    const names = new Set<string>();
    for (const b of skeleton.bones) {
      names.add(this.normalizeForMatch(b.name, prefix, suffix));
    }
    return names;
  }

  private getSignatureBones(): Set<string> {
    const result = new Set<string>();
    for (const b of this.signatureBones) {
      result.add(this.normalizeName(b));
    }
    return result;
  }

  matchScore(
    skeleton: Skeleton,
    prefix: string | null = null,
    suffix: SuffixPattern | null = null,
  ): number {
    const boneNames = this.getBoneNames(skeleton, prefix, suffix);
    const sigBones = this.getSignatureBones();

    if (sigBones.size === 0) return 0.0;

    let matches = 0;
    for (const sig of sigBones) {
      if (boneNames.has(sig)) matches++;
    }
    return matches / sigBones.size;
  }

  matchScoreWithPrefixDetection(
    skeleton: Skeleton,
  ): [number, string | null, SuffixPattern | null] {
    const boneNames = skeleton.bones.map((b) => b.name);

    let bestScore = this.matchScore(skeleton, null, null);
    let bestPrefix: string | null = null;
    let bestSuffix: SuffixPattern | null = null;

    const detectedPrefix = detectCommonPrefix(boneNames);
    const namesAfterPrefix = boneNames.map((n) =>
      detectedPrefix ? stripPrefix(n, detectedPrefix) : n,
    );
    const detectedSuffix = detectCommonSuffix(namesAfterPrefix);

    if (detectedPrefix) {
      const scorePrefix = this.matchScore(skeleton, detectedPrefix, null);
      if (scorePrefix > bestScore) {
        bestScore = scorePrefix;
        bestPrefix = detectedPrefix;
        bestSuffix = null;
      }
    }

    if (detectedSuffix) {
      const scoreSuffix = this.matchScore(skeleton, null, detectedSuffix);
      if (scoreSuffix > bestScore) {
        bestScore = scoreSuffix;
        bestPrefix = null;
        bestSuffix = detectedSuffix;
      }
    }

    if (detectedPrefix && detectedSuffix) {
      const scoreBoth = this.matchScore(skeleton, detectedPrefix, detectedSuffix);
      if (scoreBoth > bestScore) {
        bestScore = scoreBoth;
        bestPrefix = detectedPrefix;
        bestSuffix = detectedSuffix;
      }
    }

    return [bestScore, bestPrefix, bestSuffix];
  }

  getBoneIndex(
    skeleton: Skeleton,
    boneName: string,
    prefix: string | null = null,
    suffixPattern: SuffixPattern | null = null,
  ): number | null {
    const target = this.normalizeName(boneName);
    let bestIdx: number | null = null;
    let bestScore = -1;

    for (const bone of skeleton.bones) {
      const normalized = this.normalizeForMatch(bone.name, prefix, suffixPattern);
      const normalizedWithoutNumber = stripTerminalNumberToken(normalized);
      const normalizedWithoutLeadingNoise = stripLeadingMatchNoise(normalized);
      const normalizedWithoutLeadingNoiseOrNumber = stripLeadingMatchNoise(
        normalizedWithoutNumber,
      );

      let score = -1;
      if (normalized === target) score = 30;
      else if (normalizedWithoutNumber === target) score = 24;
      else if (normalizedWithoutLeadingNoise === target) score = 22;
      else if (normalizedWithoutLeadingNoiseOrNumber === target) score = 18;

      if (score < 0) continue;
      // Preset matching starts from naming, but when duplicate bones exist we
      // prefer the deforming/skinned copy over an unskinned cosmetic duplicate
      // even if the latter has the cleaner literal name.
      if (bone.deforming) score += 15;
      if (!bone.auxiliary) score += 2;

      if (score > bestScore) {
        bestScore = score;
        bestIdx = bone.index;
      }
    }

    return bestIdx;
  }

  getChainMappings(
    skeleton: Skeleton,
    prefix: string | null = null,
    suffixPattern: SuffixPattern | null = null,
  ): Record<string, [number, number, string | null]> {
    const result: Record<string, [number, number, string | null]> = {};
    for (const chain of this.chains) {
      const startIdx = this.getBoneIndex(skeleton, chain.startBone, prefix, suffixPattern);
      let endIdx = this.getBoneIndex(skeleton, chain.endBone, prefix, suffixPattern);
      if (startIdx !== null && endIdx === null) {
        if (isFingerChainName(chain.name)) {
          endIdx = inferFingerChainEnd(skeleton, startIdx);
        } else {
          endIdx = inferPresetChainEnd(skeleton, chain.name, startIdx);
        }
      }
      if (startIdx !== null && endIdx !== null) {
        result[chain.name] = [startIdx, endIdx, chain.ikGoal ?? null];
      }
    }
    return result;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// DETECTED PRESET
// ═══════════════════════════════════════════════════════════════════════

export interface DetectedPreset {
  preset: RigPreset;
  score: number;
  prefix: string | null;
  suffixPattern: SuffixPattern | null;
}

export function detectedPresetName(dp: DetectedPreset): string {
  if (dp.prefix) return `${dp.preset.name} (prefixed: ${dp.prefix})`;
  return dp.preset.name;
}

export function detectedPresetChainMappings(
  dp: DetectedPreset,
  skeleton: Skeleton,
): Record<string, [number, number, string | null]> {
  return dp.preset.getChainMappings(skeleton, dp.prefix, dp.suffixPattern);
}

// ═══════════════════════════════════════════════════════════════════════
// DETECTION
// ═══════════════════════════════════════════════════════════════════════

export function detectRigPreset(
  skeleton: Skeleton,
  minMatchScore = 0.85,
  customPresets?: RigPreset[],
): DetectedPreset | null {
  const allPresets = [...ALL_PRESETS];
  if (customPresets) allPresets.push(...customPresets);

  let bestResult: DetectedPreset | null = null;
  let bestScore = 0.0;
  let bestSignatureSize = -1;

  for (const preset of allPresets) {
    const [score, prefix, suffixPattern] = preset.matchScoreWithPrefixDetection(skeleton);

    if (score >= minMatchScore) {
      const sigSize = preset.signatureBones.size;
      if (score > bestScore || (score === bestScore && sigSize > bestSignatureSize)) {
        bestScore = score;
        bestSignatureSize = sigSize;
        bestResult = { preset, score, prefix, suffixPattern };
      }
    }
  }

  return bestResult;
}

// ═══════════════════════════════════════════════════════════════════════
// PRESET DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════

export const MIXAMO_PRESET = new RigPreset({
  name: 'Mixamo',
  id: 'mixamo',
  description: 'Adobe Mixamo standard humanoid skeleton',
  caseSensitive: true,
  confidenceBoost: 0.15,
  signatureBones: [
    'Hips',
    'Spine', 'Spine1', 'Spine2',
    'Neck', 'Head',
    'LeftShoulder', 'LeftArm', 'LeftForeArm', 'LeftHand',
    'RightShoulder', 'RightArm', 'RightForeArm', 'RightHand',
    'LeftUpLeg', 'LeftLeg', 'LeftFoot', 'LeftToeBase',
    'RightUpLeg', 'RightLeg', 'RightFoot', 'RightToeBase',
  ],
  optionalBones: [
    'HeadTop_End', 'LeftToe_End', 'RightToe_End',
    'LeftHandThumb1', 'LeftHandThumb2', 'LeftHandThumb3', 'LeftHandThumb4',
    'LeftHandIndex1', 'LeftHandIndex2', 'LeftHandIndex3', 'LeftHandIndex4',
    'LeftHandMiddle1', 'LeftHandMiddle2', 'LeftHandMiddle3', 'LeftHandMiddle4',
    'LeftHandRing1', 'LeftHandRing2', 'LeftHandRing3', 'LeftHandRing4',
    'LeftHandPinky1', 'LeftHandPinky2', 'LeftHandPinky3', 'LeftHandPinky4',
    'RightHandThumb1', 'RightHandThumb2', 'RightHandThumb3', 'RightHandThumb4',
    'RightHandIndex1', 'RightHandIndex2', 'RightHandIndex3', 'RightHandIndex4',
    'RightHandMiddle1', 'RightHandMiddle2', 'RightHandMiddle3', 'RightHandMiddle4',
    'RightHandRing1', 'RightHandRing2', 'RightHandRing3', 'RightHandRing4',
    'RightHandPinky1', 'RightHandPinky2', 'RightHandPinky3', 'RightHandPinky4',
  ],
  chains: [
    C('Pelvis', 'Hips', 'Hips'),
    C('Spine', 'Spine', 'Spine2'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'LeftShoulder', 'LeftShoulder'),
    C('RightClavicle', 'RightShoulder', 'RightShoulder'),
    C('LeftArm', 'LeftArm', 'LeftHand', 'LeftHand_Goal'),
    C('RightArm', 'RightArm', 'RightHand', 'RightHand_Goal'),
    C('LeftLeg', 'LeftUpLeg', 'LeftFoot', 'LeftFoot_Goal'),
    C('RightLeg', 'RightUpLeg', 'RightFoot', 'RightFoot_Goal'),
    C('LeftToe', 'LeftToeBase', 'LeftToeBase'),
    C('RightToe', 'RightToeBase', 'RightToeBase'),
    C('LeftThumb', 'LeftHandThumb1', 'LeftHandThumb3'),
    C('LeftIndex', 'LeftHandIndex1', 'LeftHandIndex3'),
    C('LeftMiddle', 'LeftHandMiddle1', 'LeftHandMiddle3'),
    C('LeftRing', 'LeftHandRing1', 'LeftHandRing3'),
    C('LeftPinky', 'LeftHandPinky1', 'LeftHandPinky3'),
    C('RightThumb', 'RightHandThumb1', 'RightHandThumb3'),
    C('RightIndex', 'RightHandIndex1', 'RightHandIndex3'),
    C('RightMiddle', 'RightHandMiddle1', 'RightHandMiddle3'),
    C('RightRing', 'RightHandRing1', 'RightHandRing3'),
    C('RightPinky', 'RightHandPinky1', 'RightHandPinky3'),
  ],
});

export const UE5_MANNEQUIN_PRESET = new RigPreset({
  name: 'UE5 Mannequin',
  id: 'ue5_mannequin',
  description: 'Unreal Engine 5 standard humanoid skeleton (Manny/Quinn)',
  caseSensitive: false,
  confidenceBoost: 0.15,
  signatureBones: [
    'root', 'pelvis',
    'spine_01', 'spine_02', 'spine_03', 'spine_04', 'spine_05',
    'neck_01', 'neck_02', 'head',
    'clavicle_l', 'upperarm_l', 'lowerarm_l', 'hand_l',
    'clavicle_r', 'upperarm_r', 'lowerarm_r', 'hand_r',
    'thigh_l', 'calf_l', 'foot_l', 'ball_l',
    'thigh_r', 'calf_r', 'foot_r', 'ball_r',
  ],
  optionalBones: [
    'ik_foot_root', 'ik_foot_l', 'ik_foot_r',
    'ik_hand_root', 'ik_hand_gun', 'ik_hand_l', 'ik_hand_r',
    'upperarm_twist_01_l', 'upperarm_twist_02_l',
    'lowerarm_twist_01_l', 'lowerarm_twist_02_l',
    'upperarm_twist_01_r', 'upperarm_twist_02_r',
    'lowerarm_twist_01_r', 'lowerarm_twist_02_r',
    'thigh_twist_01_l', 'thigh_twist_01_r',
    'calf_twist_01_l', 'calf_twist_01_r',
    'thumb_01_l', 'thumb_02_l', 'thumb_03_l',
    'index_metacarpal_l', 'index_01_l', 'index_02_l', 'index_03_l',
    'middle_metacarpal_l', 'middle_01_l', 'middle_02_l', 'middle_03_l',
    'ring_metacarpal_l', 'ring_01_l', 'ring_02_l', 'ring_03_l',
    'pinky_metacarpal_l', 'pinky_01_l', 'pinky_02_l', 'pinky_03_l',
    'thumb_01_r', 'thumb_02_r', 'thumb_03_r',
    'index_metacarpal_r', 'index_01_r', 'index_02_r', 'index_03_r',
    'middle_metacarpal_r', 'middle_01_r', 'middle_02_r', 'middle_03_r',
    'ring_metacarpal_r', 'ring_01_r', 'ring_02_r', 'ring_03_r',
    'pinky_metacarpal_r', 'pinky_01_r', 'pinky_02_r', 'pinky_03_r',
  ],
  chains: [
    C('Root', 'root', 'root'),
    C('Pelvis', 'pelvis', 'pelvis'),
    C('Spine', 'spine_01', 'spine_05'),
    C('Neck', 'neck_01', 'neck_02'),
    C('Head', 'head', 'head'),
    C('LeftClavicle', 'clavicle_l', 'clavicle_l'),
    C('RightClavicle', 'clavicle_r', 'clavicle_r'),
    C('LeftArm', 'upperarm_l', 'hand_l', 'hand_l_Goal'),
    C('RightArm', 'upperarm_r', 'hand_r', 'hand_r_Goal'),
    C('LeftLeg', 'thigh_l', 'foot_l', 'foot_l_Goal'),
    C('RightLeg', 'thigh_r', 'foot_r', 'foot_r_Goal'),
    C('LeftToe', 'ball_l', 'ball_l'),
    C('RightToe', 'ball_r', 'ball_r'),
    C('LeftThumb', 'thumb_01_l', 'thumb_03_l'),
    C('LeftIndex', 'index_01_l', 'index_03_l'),
    C('LeftMiddle', 'middle_01_l', 'middle_03_l'),
    C('LeftRing', 'ring_01_l', 'ring_03_l'),
    C('LeftPinky', 'pinky_01_l', 'pinky_03_l'),
    C('RightThumb', 'thumb_01_r', 'thumb_03_r'),
    C('RightIndex', 'index_01_r', 'index_03_r'),
    C('RightMiddle', 'middle_01_r', 'middle_03_r'),
    C('RightRing', 'ring_01_r', 'ring_03_r'),
    C('RightPinky', 'pinky_01_r', 'pinky_03_r'),
    C('LeftIndexMetacarpal', 'index_metacarpal_l', 'index_metacarpal_l'),
    C('LeftMiddleMetacarpal', 'middle_metacarpal_l', 'middle_metacarpal_l'),
    C('LeftRingMetacarpal', 'ring_metacarpal_l', 'ring_metacarpal_l'),
    C('LeftPinkyMetacarpal', 'pinky_metacarpal_l', 'pinky_metacarpal_l'),
    C('RightIndexMetacarpal', 'index_metacarpal_r', 'index_metacarpal_r'),
    C('RightMiddleMetacarpal', 'middle_metacarpal_r', 'middle_metacarpal_r'),
    C('RightRingMetacarpal', 'ring_metacarpal_r', 'ring_metacarpal_r'),
    C('RightPinkyMetacarpal', 'pinky_metacarpal_r', 'pinky_metacarpal_r'),
  ],
});

export const UE4_MANNEQUIN_PRESET = new RigPreset({
  name: 'UE4 Mannequin',
  id: 'ue4_mannequin',
  description: 'Unreal Engine 4 standard humanoid skeleton',
  caseSensitive: false,
  confidenceBoost: 0.15,
  signatureBones: [
    'root', 'pelvis',
    'spine_01', 'spine_02', 'spine_03',
    'neck_01', 'head',
    'clavicle_l', 'upperarm_l', 'lowerarm_l', 'hand_l',
    'clavicle_r', 'upperarm_r', 'lowerarm_r', 'hand_r',
    'thigh_l', 'calf_l', 'foot_l', 'ball_l',
    'thigh_r', 'calf_r', 'foot_r', 'ball_r',
  ],
  optionalBones: [
    'ik_foot_root', 'ik_foot_l', 'ik_foot_r',
    'ik_hand_root', 'ik_hand_gun', 'ik_hand_l', 'ik_hand_r',
    'upperarm_twist_01_l', 'lowerarm_twist_01_l',
    'upperarm_twist_01_r', 'lowerarm_twist_01_r',
    'thigh_twist_01_l', 'thigh_twist_01_r',
    'thumb_01_l', 'thumb_02_l', 'thumb_03_l',
    'index_01_l', 'index_02_l', 'index_03_l',
    'middle_01_l', 'middle_02_l', 'middle_03_l',
    'ring_01_l', 'ring_02_l', 'ring_03_l',
    'pinky_01_l', 'pinky_02_l', 'pinky_03_l',
    'thumb_01_r', 'thumb_02_r', 'thumb_03_r',
    'index_01_r', 'index_02_r', 'index_03_r',
    'middle_01_r', 'middle_02_r', 'middle_03_r',
    'ring_01_r', 'ring_02_r', 'ring_03_r',
    'pinky_01_r', 'pinky_02_r', 'pinky_03_r',
  ],
  chains: [
    C('Root', 'root', 'root'),
    C('Pelvis', 'pelvis', 'pelvis'),
    C('Spine', 'spine_01', 'spine_03'),
    C('Neck', 'neck_01', 'neck_01'),
    C('Head', 'head', 'head'),
    C('LeftClavicle', 'clavicle_l', 'clavicle_l'),
    C('RightClavicle', 'clavicle_r', 'clavicle_r'),
    C('LeftArm', 'upperarm_l', 'hand_l', 'hand_l_Goal'),
    C('RightArm', 'upperarm_r', 'hand_r', 'hand_r_Goal'),
    C('LeftLeg', 'thigh_l', 'foot_l', 'foot_l_Goal'),
    C('RightLeg', 'thigh_r', 'foot_r', 'foot_r_Goal'),
    C('LeftToe', 'ball_l', 'ball_l'),
    C('RightToe', 'ball_r', 'ball_r'),
    C('LeftThumb', 'thumb_01_l', 'thumb_03_l'),
    C('LeftIndex', 'index_01_l', 'index_03_l'),
    C('LeftMiddle', 'middle_01_l', 'middle_03_l'),
    C('LeftRing', 'ring_01_l', 'ring_03_l'),
    C('LeftPinky', 'pinky_01_l', 'pinky_03_l'),
    C('RightThumb', 'thumb_01_r', 'thumb_03_r'),
    C('RightIndex', 'index_01_r', 'index_03_r'),
    C('RightMiddle', 'middle_01_r', 'middle_03_r'),
    C('RightRing', 'ring_01_r', 'ring_03_r'),
    C('RightPinky', 'pinky_01_r', 'pinky_03_r'),
  ],
});

export const BVH_STANDARD_PRESET = new RigPreset({
  name: 'BVH Standard',
  id: 'bvh_standard',
  description: 'Common BVH motion capture naming convention',
  caseSensitive: false,
  confidenceBoost: 0.10,
  signatureBones: [
    'Hips',
    'Spine', 'Spine1', 'Spine2',
    'Neck', 'Neck1', 'Head',
    'LeftShoulder', 'LeftArm', 'LeftForeArm', 'LeftHand',
    'RightShoulder', 'RightArm', 'RightForeArm', 'RightHand',
    'LeftUpLeg', 'LeftLeg', 'LeftFoot',
    'RightUpLeg', 'RightLeg', 'RightFoot',
  ],
  optionalBones: [
    'Chest', 'UpperChest',
    'LeftToeBase', 'RightToeBase',
    'LeftToe_End', 'RightToe_End',
    'Head_End', 'LeftHand_End', 'RightHand_End',
    'LHipJoint', 'RHipJoint',
    'LeftHip', 'RightHip',
  ],
  chains: [
    C('Spine', 'Spine', 'Spine2'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'LeftShoulder', 'LeftShoulder'),
    C('RightClavicle', 'RightShoulder', 'RightShoulder'),
    C('LeftArm', 'LeftArm', 'LeftHand'),
    C('RightArm', 'RightArm', 'RightHand'),
    C('LeftLeg', 'LeftUpLeg', 'LeftFoot'),
    C('RightLeg', 'RightUpLeg', 'RightFoot'),
    C('LeftToe', 'LeftToeBase', 'LeftToeBase'),
    C('RightToe', 'RightToeBase', 'RightToeBase'),
  ],
});

export const RIGIFY_PRESET = new RigPreset({
  name: 'Rigify',
  id: 'rigify',
  description: 'Blender Rigify auto-rigging addon',
  caseSensitive: false,
  confidenceBoost: 0.15,
  signatureBones: [
    'DEF-spine', 'DEF-spine.001', 'DEF-spine.002', 'DEF-spine.003',
    'DEF-spine.004', 'DEF-spine.005', 'DEF-spine.006',
    'DEF-shoulder.L', 'DEF-upper_arm.L', 'DEF-forearm.L', 'DEF-hand.L',
    'DEF-shoulder.R', 'DEF-upper_arm.R', 'DEF-forearm.R', 'DEF-hand.R',
    'DEF-thigh.L', 'DEF-shin.L', 'DEF-foot.L', 'DEF-toe.L',
    'DEF-thigh.R', 'DEF-shin.R', 'DEF-foot.R', 'DEF-toe.R',
  ],
  optionalBones: [
    'DEF-upper_arm.L.001', 'DEF-forearm.L.001',
    'DEF-upper_arm.R.001', 'DEF-forearm.R.001',
    'DEF-thigh.L.001', 'DEF-shin.L.001',
    'DEF-thigh.R.001', 'DEF-shin.R.001',
    'DEF-thumb.01.L', 'DEF-thumb.02.L', 'DEF-thumb.03.L',
    'DEF-f_index.01.L', 'DEF-f_index.02.L', 'DEF-f_index.03.L',
    'DEF-f_middle.01.L', 'DEF-f_middle.02.L', 'DEF-f_middle.03.L',
    'DEF-f_ring.01.L', 'DEF-f_ring.02.L', 'DEF-f_ring.03.L',
    'DEF-f_pinky.01.L', 'DEF-f_pinky.02.L', 'DEF-f_pinky.03.L',
    'DEF-thumb.01.R', 'DEF-thumb.02.R', 'DEF-thumb.03.R',
    'DEF-f_index.01.R', 'DEF-f_index.02.R', 'DEF-f_index.03.R',
    'DEF-f_middle.01.R', 'DEF-f_middle.02.R', 'DEF-f_middle.03.R',
    'DEF-f_ring.01.R', 'DEF-f_ring.02.R', 'DEF-f_ring.03.R',
    'DEF-f_pinky.01.R', 'DEF-f_pinky.02.R', 'DEF-f_pinky.03.R',
  ],
  chains: [
    C('Spine', 'DEF-spine', 'DEF-spine.003'),
    C('Neck', 'DEF-spine.004', 'DEF-spine.005'),
    C('Head', 'DEF-spine.006', 'DEF-spine.006'),
    C('LeftClavicle', 'DEF-shoulder.L', 'DEF-shoulder.L'),
    C('RightClavicle', 'DEF-shoulder.R', 'DEF-shoulder.R'),
    C('LeftArm', 'DEF-upper_arm.L', 'DEF-hand.L', 'hand.L_Goal'),
    C('RightArm', 'DEF-upper_arm.R', 'DEF-hand.R', 'hand.R_Goal'),
    C('LeftLeg', 'DEF-thigh.L', 'DEF-foot.L', 'foot.L_Goal'),
    C('RightLeg', 'DEF-thigh.R', 'DEF-foot.R', 'foot.R_Goal'),
    C('LeftToe', 'DEF-toe.L', 'DEF-toe.L'),
    C('RightToe', 'DEF-toe.R', 'DEF-toe.R'),
    C('LeftThumb', 'DEF-thumb.01.L', 'DEF-thumb.03.L'),
    C('LeftIndex', 'DEF-f_index.01.L', 'DEF-f_index.03.L'),
    C('LeftMiddle', 'DEF-f_middle.01.L', 'DEF-f_middle.03.L'),
    C('LeftRing', 'DEF-f_ring.01.L', 'DEF-f_ring.03.L'),
    C('LeftPinky', 'DEF-f_pinky.01.L', 'DEF-f_pinky.03.L'),
    C('RightThumb', 'DEF-thumb.01.R', 'DEF-thumb.03.R'),
    C('RightIndex', 'DEF-f_index.01.R', 'DEF-f_index.03.R'),
    C('RightMiddle', 'DEF-f_middle.01.R', 'DEF-f_middle.03.R'),
    C('RightRing', 'DEF-f_ring.01.R', 'DEF-f_ring.03.R'),
    C('RightPinky', 'DEF-f_pinky.01.R', 'DEF-f_pinky.03.R'),
  ],
});

export const MAYA_HUMANIK_PRESET = new RigPreset({
  name: 'Maya HumanIK',
  id: 'maya_humanik',
  description: 'Autodesk Maya HumanIK (HIK) standard',
  caseSensitive: true,
  confidenceBoost: 0.15,
  signatureBones: [
    'Reference', 'Hips',
    'Spine', 'Spine1', 'Spine2',
    'Neck', 'Head',
    'LeftShoulder', 'LeftArm', 'LeftForeArm', 'LeftHand',
    'RightShoulder', 'RightArm', 'RightForeArm', 'RightHand',
    'LeftUpLeg', 'LeftLeg', 'LeftFoot',
    'RightUpLeg', 'RightLeg', 'RightFoot',
  ],
  optionalBones: [
    'Spine3', 'Neck1',
    'LeftToeBase', 'RightToeBase',
    'LeftUpLegRoll', 'LeftLegRoll', 'RightUpLegRoll', 'RightLegRoll',
    'LeftArmRoll', 'LeftForeArmRoll', 'RightArmRoll', 'RightForeArmRoll',
    'LeftHandThumb1', 'LeftHandThumb2', 'LeftHandThumb3',
    'LeftHandIndex1', 'LeftHandIndex2', 'LeftHandIndex3',
    'LeftHandMiddle1', 'LeftHandMiddle2', 'LeftHandMiddle3',
    'LeftHandRing1', 'LeftHandRing2', 'LeftHandRing3',
    'LeftHandPinky1', 'LeftHandPinky2', 'LeftHandPinky3',
    'RightHandThumb1', 'RightHandThumb2', 'RightHandThumb3',
    'RightHandIndex1', 'RightHandIndex2', 'RightHandIndex3',
    'RightHandMiddle1', 'RightHandMiddle2', 'RightHandMiddle3',
    'RightHandRing1', 'RightHandRing2', 'RightHandRing3',
    'RightHandPinky1', 'RightHandPinky2', 'RightHandPinky3',
  ],
  chains: [
    C('Spine', 'Spine', 'Spine2'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'LeftShoulder', 'LeftShoulder'),
    C('RightClavicle', 'RightShoulder', 'RightShoulder'),
    C('LeftArm', 'LeftArm', 'LeftHand', 'LeftHand_Goal'),
    C('RightArm', 'RightArm', 'RightHand', 'RightHand_Goal'),
    C('LeftLeg', 'LeftUpLeg', 'LeftFoot', 'LeftFoot_Goal'),
    C('RightLeg', 'RightUpLeg', 'RightFoot', 'RightFoot_Goal'),
    C('LeftToe', 'LeftToeBase', 'LeftToeBase'),
    C('RightToe', 'RightToeBase', 'RightToeBase'),
    C('LeftThumb', 'LeftHandThumb1', 'LeftHandThumb3'),
    C('LeftIndex', 'LeftHandIndex1', 'LeftHandIndex3'),
    C('LeftMiddle', 'LeftHandMiddle1', 'LeftHandMiddle3'),
    C('LeftRing', 'LeftHandRing1', 'LeftHandRing3'),
    C('LeftPinky', 'LeftHandPinky1', 'LeftHandPinky3'),
    C('RightThumb', 'RightHandThumb1', 'RightHandThumb3'),
    C('RightIndex', 'RightHandIndex1', 'RightHandIndex3'),
    C('RightMiddle', 'RightHandMiddle1', 'RightHandMiddle3'),
    C('RightRing', 'RightHandRing1', 'RightHandRing3'),
    C('RightPinky', 'RightHandPinky1', 'RightHandPinky3'),
  ],
});

export const MAX_BIPED_PRESET = new RigPreset({
  name: '3ds Max Biped',
  id: 'max_biped',
  description: 'Autodesk 3ds Max Biped character system',
  caseSensitive: false,
  confidenceBoost: 0.15,
  signatureBones: [
    'Bip001', 'Bip001 Pelvis',
    'Bip001 Spine', 'Bip001 Spine1', 'Bip001 Spine2',
    'Bip001 Neck', 'Bip001 Head',
    'Bip001 L Clavicle', 'Bip001 L UpperArm', 'Bip001 L Forearm', 'Bip001 L Hand',
    'Bip001 R Clavicle', 'Bip001 R UpperArm', 'Bip001 R Forearm', 'Bip001 R Hand',
    'Bip001 L Thigh', 'Bip001 L Calf', 'Bip001 L Foot',
    'Bip001 R Thigh', 'Bip001 R Calf', 'Bip001 R Foot',
  ],
  optionalBones: [
    'Bip001 Spine3', 'Bip001 Neck1',
    'Bip001 L Toe0', 'Bip001 R Toe0',
    'Bip001 L Finger0', 'Bip001 L Finger01', 'Bip001 L Finger02',
    'Bip001 L Finger1', 'Bip001 L Finger11', 'Bip001 L Finger12',
    'Bip001 L Finger2', 'Bip001 L Finger21', 'Bip001 L Finger22',
    'Bip001 L Finger3', 'Bip001 L Finger31', 'Bip001 L Finger32',
    'Bip001 L Finger4', 'Bip001 L Finger41', 'Bip001 L Finger42',
    'Bip001 R Finger0', 'Bip001 R Finger01', 'Bip001 R Finger02',
    'Bip001 R Finger1', 'Bip001 R Finger11', 'Bip001 R Finger12',
    'Bip001 R Finger2', 'Bip001 R Finger21', 'Bip001 R Finger22',
    'Bip001 R Finger3', 'Bip001 R Finger31', 'Bip001 R Finger32',
    'Bip001 R Finger4', 'Bip001 R Finger41', 'Bip001 R Finger42',
  ],
  chains: [
    C('Root', 'Bip001', 'Bip001'),
    C('Spine', 'Bip001 Spine', 'Bip001 Spine2'),
    C('Neck', 'Bip001 Neck', 'Bip001 Neck'),
    C('Head', 'Bip001 Head', 'Bip001 Head'),
    C('LeftClavicle', 'Bip001 L Clavicle', 'Bip001 L Clavicle'),
    C('RightClavicle', 'Bip001 R Clavicle', 'Bip001 R Clavicle'),
    C('LeftArm', 'Bip001 L UpperArm', 'Bip001 L Hand'),
    C('RightArm', 'Bip001 R UpperArm', 'Bip001 R Hand'),
    C('LeftLeg', 'Bip001 L Thigh', 'Bip001 L Foot'),
    C('RightLeg', 'Bip001 R Thigh', 'Bip001 R Foot'),
    C('LeftToe', 'Bip001 L Toe0', 'Bip001 L Toe0'),
    C('RightToe', 'Bip001 R Toe0', 'Bip001 R Toe0'),
    C('LeftThumb', 'Bip001 L Finger0', 'Bip001 L Finger02'),
    C('LeftIndex', 'Bip001 L Finger1', 'Bip001 L Finger12'),
    C('LeftMiddle', 'Bip001 L Finger2', 'Bip001 L Finger22'),
    C('LeftRing', 'Bip001 L Finger3', 'Bip001 L Finger32'),
    C('LeftPinky', 'Bip001 L Finger4', 'Bip001 L Finger42'),
    C('RightThumb', 'Bip001 R Finger0', 'Bip001 R Finger02'),
    C('RightIndex', 'Bip001 R Finger1', 'Bip001 R Finger12'),
    C('RightMiddle', 'Bip001 R Finger2', 'Bip001 R Finger22'),
    C('RightRing', 'Bip001 R Finger3', 'Bip001 R Finger32'),
    C('RightPinky', 'Bip001 R Finger4', 'Bip001 R Finger42'),
  ],
});

export const MAX_CAT_PRESET = new RigPreset({
  name: '3ds Max CAT',
  id: 'max_cat',
  description: 'Autodesk 3ds Max CAT (Character Animation Toolkit)',
  caseSensitive: false,
  confidenceBoost: 0.15,
  signatureBones: [
    'CATRigHub1',
    'CATRigSpine1', 'CATRigSpine2', 'CATRigSpine3',
    'CATRigRibcage', 'CATRigNeck1', 'CATRigHead',
    'CATRigLArmCollarbone1', 'CATRigLArm1', 'CATRigLArm2', 'CATRigLArmPalm',
    'CATRigRArmCollarbone1', 'CATRigRArm1', 'CATRigRArm2', 'CATRigRArmPalm',
    'CATRigLLeg1', 'CATRigLLeg2', 'CATRigLLegAnkle',
    'CATRigRLeg1', 'CATRigRLeg2', 'CATRigRLegAnkle',
  ],
  optionalBones: [
    'CATRigSpine4', 'CATRigPelvis',
    'CATRigLLegToes', 'CATRigRLegToes',
    'CATRigLArmDigit11', 'CATRigLArmDigit12', 'CATRigLArmDigit13',
    'CATRigLArmDigit21', 'CATRigLArmDigit22', 'CATRigLArmDigit23',
    'CATRigLArmDigit31', 'CATRigLArmDigit32', 'CATRigLArmDigit33',
    'CATRigLArmDigit41', 'CATRigLArmDigit42', 'CATRigLArmDigit43',
    'CATRigLArmDigit51', 'CATRigLArmDigit52', 'CATRigLArmDigit53',
    'CATRigRArmDigit11', 'CATRigRArmDigit12', 'CATRigRArmDigit13',
    'CATRigRArmDigit21', 'CATRigRArmDigit22', 'CATRigRArmDigit23',
    'CATRigRArmDigit31', 'CATRigRArmDigit32', 'CATRigRArmDigit33',
    'CATRigRArmDigit41', 'CATRigRArmDigit42', 'CATRigRArmDigit43',
    'CATRigRArmDigit51', 'CATRigRArmDigit52', 'CATRigRArmDigit53',
  ],
  chains: [
    C('Root', 'CATRigHub1', 'CATRigHub1'),
    C('Spine', 'CATRigSpine1', 'CATRigRibcage'),
    C('Neck', 'CATRigNeck1', 'CATRigNeck1'),
    C('Head', 'CATRigHead', 'CATRigHead'),
    C('LeftClavicle', 'CATRigLArmCollarbone1', 'CATRigLArmCollarbone1'),
    C('RightClavicle', 'CATRigRArmCollarbone1', 'CATRigRArmCollarbone1'),
    C('LeftArm', 'CATRigLArm1', 'CATRigLArmPalm'),
    C('RightArm', 'CATRigRArm1', 'CATRigRArmPalm'),
    C('LeftLeg', 'CATRigLLeg1', 'CATRigLLegAnkle'),
    C('RightLeg', 'CATRigRLeg1', 'CATRigRLegAnkle'),
    C('LeftToe', 'CATRigLLegToes', 'CATRigLLegToes'),
    C('RightToe', 'CATRigRLegToes', 'CATRigRLegToes'),
    C('LeftThumb', 'CATRigLArmDigit11', 'CATRigLArmDigit13'),
    C('LeftIndex', 'CATRigLArmDigit21', 'CATRigLArmDigit23'),
    C('LeftMiddle', 'CATRigLArmDigit31', 'CATRigLArmDigit33'),
    C('LeftRing', 'CATRigLArmDigit41', 'CATRigLArmDigit43'),
    C('LeftPinky', 'CATRigLArmDigit51', 'CATRigLArmDigit53'),
    C('RightThumb', 'CATRigRArmDigit11', 'CATRigRArmDigit13'),
    C('RightIndex', 'CATRigRArmDigit21', 'CATRigRArmDigit23'),
    C('RightMiddle', 'CATRigRArmDigit31', 'CATRigRArmDigit33'),
    C('RightRing', 'CATRigRArmDigit41', 'CATRigRArmDigit43'),
    C('RightPinky', 'CATRigRArmDigit51', 'CATRigRArmDigit53'),
  ],
});

export const UNITY_HUMANOID_PRESET = new RigPreset({
  name: 'Unity Humanoid',
  id: 'unity_humanoid',
  description: 'Unity Mecanim humanoid avatar standard',
  caseSensitive: false,
  confidenceBoost: 0.15,
  signatureBones: [
    'Hips',
    'Spine', 'Chest', 'UpperChest',
    'Neck', 'Head',
    'LeftShoulder', 'LeftUpperArm', 'LeftLowerArm', 'LeftHand',
    'RightShoulder', 'RightUpperArm', 'RightLowerArm', 'RightHand',
    'LeftUpperLeg', 'LeftLowerLeg', 'LeftFoot', 'LeftToes',
    'RightUpperLeg', 'RightLowerLeg', 'RightFoot', 'RightToes',
  ],
  optionalBones: [
    'LeftEye', 'RightEye', 'Jaw',
    'LeftThumbProximal', 'LeftThumbIntermediate', 'LeftThumbDistal',
    'LeftIndexProximal', 'LeftIndexIntermediate', 'LeftIndexDistal',
    'LeftMiddleProximal', 'LeftMiddleIntermediate', 'LeftMiddleDistal',
    'LeftRingProximal', 'LeftRingIntermediate', 'LeftRingDistal',
    'LeftLittleProximal', 'LeftLittleIntermediate', 'LeftLittleDistal',
    'RightThumbProximal', 'RightThumbIntermediate', 'RightThumbDistal',
    'RightIndexProximal', 'RightIndexIntermediate', 'RightIndexDistal',
    'RightMiddleProximal', 'RightMiddleIntermediate', 'RightMiddleDistal',
    'RightRingProximal', 'RightRingIntermediate', 'RightRingDistal',
    'RightLittleProximal', 'RightLittleIntermediate', 'RightLittleDistal',
  ],
  chains: [
    C('Spine', 'Spine', 'UpperChest'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'LeftShoulder', 'LeftShoulder'),
    C('RightClavicle', 'RightShoulder', 'RightShoulder'),
    C('LeftArm', 'LeftUpperArm', 'LeftHand', 'LeftHand_Goal'),
    C('RightArm', 'RightUpperArm', 'RightHand', 'RightHand_Goal'),
    C('LeftLeg', 'LeftUpperLeg', 'LeftFoot', 'LeftFoot_Goal'),
    C('RightLeg', 'RightUpperLeg', 'RightFoot', 'RightFoot_Goal'),
    C('LeftToe', 'LeftToes', 'LeftToes'),
    C('RightToe', 'RightToes', 'RightToes'),
    C('LeftThumb', 'LeftThumbProximal', 'LeftThumbDistal'),
    C('LeftIndex', 'LeftIndexProximal', 'LeftIndexDistal'),
    C('LeftMiddle', 'LeftMiddleProximal', 'LeftMiddleDistal'),
    C('LeftRing', 'LeftRingProximal', 'LeftRingDistal'),
    C('LeftPinky', 'LeftLittleProximal', 'LeftLittleDistal'),
    C('RightThumb', 'RightThumbProximal', 'RightThumbDistal'),
    C('RightIndex', 'RightIndexProximal', 'RightIndexDistal'),
    C('RightMiddle', 'RightMiddleProximal', 'RightMiddleDistal'),
    C('RightRing', 'RightRingProximal', 'RightRingDistal'),
    C('RightPinky', 'RightLittleProximal', 'RightLittleDistal'),
  ],
});

export const MOTIONBUILDER_PRESET = new RigPreset({
  name: 'MotionBuilder Actor',
  id: 'motionbuilder_actor',
  description: 'Autodesk MotionBuilder Actor template',
  caseSensitive: true,
  confidenceBoost: 0.12,
  signatureBones: [
    'Hips', 'Spine', 'Spine1', 'Spine2', 'Spine3', 'Neck', 'Head',
    'LeftShoulder', 'LeftArm', 'LeftForeArm', 'LeftHand',
    'RightShoulder', 'RightArm', 'RightForeArm', 'RightHand',
    'LeftUpLeg', 'LeftLeg', 'LeftFoot',
    'RightUpLeg', 'RightLeg', 'RightFoot',
  ],
  optionalBones: [
    'Reference', 'LeftToeBase', 'RightToeBase',
    'LeftUpLegRoll', 'LeftLegRoll', 'RightUpLegRoll', 'RightLegRoll',
    'LeftArmRoll', 'LeftForeArmRoll', 'RightArmRoll', 'RightForeArmRoll',
    'LeftHandThumb1', 'LeftHandThumb2', 'LeftHandThumb3',
    'LeftHandIndex1', 'LeftHandIndex2', 'LeftHandIndex3',
    'LeftHandMiddle1', 'LeftHandMiddle2', 'LeftHandMiddle3',
    'LeftHandRing1', 'LeftHandRing2', 'LeftHandRing3',
    'LeftHandPinky1', 'LeftHandPinky2', 'LeftHandPinky3',
    'RightHandThumb1', 'RightHandThumb2', 'RightHandThumb3',
    'RightHandIndex1', 'RightHandIndex2', 'RightHandIndex3',
    'RightHandMiddle1', 'RightHandMiddle2', 'RightHandMiddle3',
    'RightHandRing1', 'RightHandRing2', 'RightHandRing3',
    'RightHandPinky1', 'RightHandPinky2', 'RightHandPinky3',
  ],
  chains: [
    C('Spine', 'Spine', 'Spine3'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'LeftShoulder', 'LeftShoulder'),
    C('RightClavicle', 'RightShoulder', 'RightShoulder'),
    C('LeftArm', 'LeftArm', 'LeftHand'),
    C('RightArm', 'RightArm', 'RightHand'),
    C('LeftLeg', 'LeftUpLeg', 'LeftFoot'),
    C('RightLeg', 'RightUpLeg', 'RightFoot'),
    C('LeftToe', 'LeftToeBase', 'LeftToeBase'),
    C('RightToe', 'RightToeBase', 'RightToeBase'),
  ],
});

export const ICLONE_CC_PRESET = new RigPreset({
  name: 'iClone/Character Creator',
  id: 'iclone_cc',
  description: 'Reallusion iClone and Character Creator',
  caseSensitive: true,
  confidenceBoost: 0.15,
  signatureBones: [
    'CC_Base_Hip',
    'CC_Base_Waist', 'CC_Base_Spine01', 'CC_Base_Spine02',
    'CC_Base_NeckTwist01', 'CC_Base_NeckTwist02', 'CC_Base_Head',
    'CC_Base_L_Clavicle', 'CC_Base_L_Upperarm', 'CC_Base_L_Forearm', 'CC_Base_L_Hand',
    'CC_Base_R_Clavicle', 'CC_Base_R_Upperarm', 'CC_Base_R_Forearm', 'CC_Base_R_Hand',
    'CC_Base_L_Thigh', 'CC_Base_L_Calf', 'CC_Base_L_Foot',
    'CC_Base_R_Thigh', 'CC_Base_R_Calf', 'CC_Base_R_Foot',
  ],
  optionalBones: [
    'CC_Base_Pelvis',
    'CC_Base_L_ToeBase', 'CC_Base_R_ToeBase',
    'CC_Base_L_BigToe1', 'CC_Base_R_BigToe1',
    'CC_Base_L_UpperarmTwist01', 'CC_Base_L_UpperarmTwist02',
    'CC_Base_L_ForearmTwist01', 'CC_Base_L_ForearmTwist02',
    'CC_Base_R_UpperarmTwist01', 'CC_Base_R_UpperarmTwist02',
    'CC_Base_R_ForearmTwist01', 'CC_Base_R_ForearmTwist02',
    'CC_Base_L_ThighTwist01', 'CC_Base_L_CalfTwist01',
    'CC_Base_R_ThighTwist01', 'CC_Base_R_CalfTwist01',
    'CC_Base_L_Thumb1', 'CC_Base_L_Thumb2', 'CC_Base_L_Thumb3',
    'CC_Base_L_Index1', 'CC_Base_L_Index2', 'CC_Base_L_Index3',
    'CC_Base_L_Mid1', 'CC_Base_L_Mid2', 'CC_Base_L_Mid3',
    'CC_Base_L_Ring1', 'CC_Base_L_Ring2', 'CC_Base_L_Ring3',
    'CC_Base_L_Pinky1', 'CC_Base_L_Pinky2', 'CC_Base_L_Pinky3',
    'CC_Base_R_Thumb1', 'CC_Base_R_Thumb2', 'CC_Base_R_Thumb3',
    'CC_Base_R_Index1', 'CC_Base_R_Index2', 'CC_Base_R_Index3',
    'CC_Base_R_Mid1', 'CC_Base_R_Mid2', 'CC_Base_R_Mid3',
    'CC_Base_R_Ring1', 'CC_Base_R_Ring2', 'CC_Base_R_Ring3',
    'CC_Base_R_Pinky1', 'CC_Base_R_Pinky2', 'CC_Base_R_Pinky3',
  ],
  chains: [
    C('Spine', 'CC_Base_Waist', 'CC_Base_Spine02'),
    C('Neck', 'CC_Base_NeckTwist01', 'CC_Base_NeckTwist02'),
    C('Head', 'CC_Base_Head', 'CC_Base_Head'),
    C('LeftClavicle', 'CC_Base_L_Clavicle', 'CC_Base_L_Clavicle'),
    C('RightClavicle', 'CC_Base_R_Clavicle', 'CC_Base_R_Clavicle'),
    C('LeftArm', 'CC_Base_L_Upperarm', 'CC_Base_L_Hand'),
    C('RightArm', 'CC_Base_R_Upperarm', 'CC_Base_R_Hand'),
    C('LeftLeg', 'CC_Base_L_Thigh', 'CC_Base_L_Foot'),
    C('RightLeg', 'CC_Base_R_Thigh', 'CC_Base_R_Foot'),
    C('LeftToe', 'CC_Base_L_ToeBase', 'CC_Base_L_ToeBase'),
    C('RightToe', 'CC_Base_R_ToeBase', 'CC_Base_R_ToeBase'),
    C('LeftThumb', 'CC_Base_L_Thumb1', 'CC_Base_L_Thumb3'),
    C('LeftIndex', 'CC_Base_L_Index1', 'CC_Base_L_Index3'),
    C('LeftMiddle', 'CC_Base_L_Mid1', 'CC_Base_L_Mid3'),
    C('LeftRing', 'CC_Base_L_Ring1', 'CC_Base_L_Ring3'),
    C('LeftPinky', 'CC_Base_L_Pinky1', 'CC_Base_L_Pinky3'),
    C('RightThumb', 'CC_Base_R_Thumb1', 'CC_Base_R_Thumb3'),
    C('RightIndex', 'CC_Base_R_Index1', 'CC_Base_R_Index3'),
    C('RightMiddle', 'CC_Base_R_Mid1', 'CC_Base_R_Mid3'),
    C('RightRing', 'CC_Base_R_Ring1', 'CC_Base_R_Ring3'),
    C('RightPinky', 'CC_Base_R_Pinky1', 'CC_Base_R_Pinky3'),
  ],
});

export const DAZ_GENESIS_PRESET = new RigPreset({
  name: 'DAZ Genesis 8/9',
  id: 'daz_genesis',
  description: 'DAZ Studio Genesis 8/9 humanoid figures',
  caseSensitive: false,
  confidenceBoost: 0.15,
  signatureBones: [
    'hip', 'abdomenLower', 'abdomenUpper', 'chestLower', 'chestUpper',
    'neckLower', 'neckUpper', 'head',
    'lCollar', 'lShldrBend', 'lForearmBend', 'lHand',
    'rCollar', 'rShldrBend', 'rForearmBend', 'rHand',
    'lThighBend', 'lShin', 'lFoot',
    'rThighBend', 'rShin', 'rFoot',
  ],
  optionalBones: [
    'pelvis', 'lToe', 'rToe', 'lBigToe', 'rBigToe', 'lSmallToe1', 'rSmallToe1',
    'lShldrTwist', 'lForearmTwist', 'rShldrTwist', 'rForearmTwist',
    'lThighTwist', 'rThighTwist',
    'lCarpal1', 'lCarpal2', 'lCarpal3', 'lCarpal4',
    'lThumb1', 'lThumb2', 'lThumb3',
    'lIndex1', 'lIndex2', 'lIndex3',
    'lMid1', 'lMid2', 'lMid3',
    'lRing1', 'lRing2', 'lRing3',
    'lPinky1', 'lPinky2', 'lPinky3',
    'rCarpal1', 'rCarpal2', 'rCarpal3', 'rCarpal4',
    'rThumb1', 'rThumb2', 'rThumb3',
    'rIndex1', 'rIndex2', 'rIndex3',
    'rMid1', 'rMid2', 'rMid3',
    'rRing1', 'rRing2', 'rRing3',
    'rPinky1', 'rPinky2', 'rPinky3',
    'lEye', 'rEye', 'lowerJaw', 'tongue01',
  ],
  chains: [
    C('Spine', 'abdomenLower', 'chestUpper'),
    C('Neck', 'neckLower', 'neckUpper'),
    C('Head', 'head', 'head'),
    C('LeftClavicle', 'lCollar', 'lCollar'),
    C('RightClavicle', 'rCollar', 'rCollar'),
    C('LeftArm', 'lShldrBend', 'lHand'),
    C('RightArm', 'rShldrBend', 'rHand'),
    C('LeftLeg', 'lThighBend', 'lFoot'),
    C('RightLeg', 'rThighBend', 'rFoot'),
    C('LeftToe', 'lToe', 'lToe'),
    C('RightToe', 'rToe', 'rToe'),
    C('LeftThumb', 'lThumb1', 'lThumb3'),
    C('LeftIndex', 'lIndex1', 'lIndex3'),
    C('LeftMiddle', 'lMid1', 'lMid3'),
    C('LeftRing', 'lRing1', 'lRing3'),
    C('LeftPinky', 'lPinky1', 'lPinky3'),
    C('RightThumb', 'rThumb1', 'rThumb3'),
    C('RightIndex', 'rIndex1', 'rIndex3'),
    C('RightMiddle', 'rMid1', 'rMid3'),
    C('RightRing', 'rRing1', 'rRing3'),
    C('RightPinky', 'rPinky1', 'rPinky3'),
  ],
});

export const CINEMA4D_PRESET = new RigPreset({
  name: 'Cinema 4D Character',
  id: 'cinema4d_character',
  description: 'Maxon Cinema 4D Character Object',
  caseSensitive: true,
  confidenceBoost: 0.12,
  signatureBones: [
    'Pelvis',
    'Spine_1', 'Spine_2', 'Spine_3', 'Spine_4',
    'Neck', 'Head',
    'L_Shoulder', 'L_Arm', 'L_Forearm', 'L_Hand',
    'R_Shoulder', 'R_Arm', 'R_Forearm', 'R_Hand',
    'L_Thigh', 'L_Shin', 'L_Foot',
    'R_Thigh', 'R_Shin', 'R_Foot',
  ],
  optionalBones: [
    'L_Toe', 'R_Toe',
    'L_Thumb_1', 'L_Thumb_2', 'L_Thumb_3',
    'L_Index_1', 'L_Index_2', 'L_Index_3',
    'L_Middle_1', 'L_Middle_2', 'L_Middle_3',
    'L_Ring_1', 'L_Ring_2', 'L_Ring_3',
    'L_Pinky_1', 'L_Pinky_2', 'L_Pinky_3',
    'R_Thumb_1', 'R_Thumb_2', 'R_Thumb_3',
    'R_Index_1', 'R_Index_2', 'R_Index_3',
    'R_Middle_1', 'R_Middle_2', 'R_Middle_3',
    'R_Ring_1', 'R_Ring_2', 'R_Ring_3',
    'R_Pinky_1', 'R_Pinky_2', 'R_Pinky_3',
  ],
  chains: [
    C('Root', 'Pelvis', 'Pelvis'),
    C('Spine', 'Spine_1', 'Spine_4'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'L_Shoulder', 'L_Shoulder'),
    C('RightClavicle', 'R_Shoulder', 'R_Shoulder'),
    C('LeftArm', 'L_Arm', 'L_Hand'),
    C('RightArm', 'R_Arm', 'R_Hand'),
    C('LeftLeg', 'L_Thigh', 'L_Foot'),
    C('RightLeg', 'R_Thigh', 'R_Foot'),
    C('LeftToe', 'L_Toe', 'L_Toe'),
    C('RightToe', 'R_Toe', 'R_Toe'),
  ],
});

export const CMU_MOCAP_PRESET = new RigPreset({
  name: 'CMU MoCap',
  id: 'cmu_mocap',
  description: 'Carnegie Mellon University motion capture format',
  caseSensitive: false,
  confidenceBoost: 0.12,
  signatureBones: [
    'root', 'lowerback', 'upperback', 'thorax',
    'lowerneck', 'upperneck', 'head',
    'lclavicle', 'lhumerus', 'lradius', 'lwrist', 'lhand',
    'rclavicle', 'rhumerus', 'rradius', 'rwrist', 'rhand',
    'lhipjoint', 'lfemur', 'ltibia', 'lfoot',
    'rhipjoint', 'rfemur', 'rtibia', 'rfoot',
  ],
  optionalBones: ['ltoes', 'rtoes', 'lthumb', 'lfingers', 'rthumb', 'rfingers'],
  chains: [
    C('Root', 'root', 'root'),
    C('Spine', 'lowerback', 'thorax'),
    C('Neck', 'lowerneck', 'upperneck'),
    C('Head', 'head', 'head'),
    C('LeftClavicle', 'lclavicle', 'lclavicle'),
    C('RightClavicle', 'rclavicle', 'rclavicle'),
    C('LeftArm', 'lhumerus', 'lhand'),
    C('RightArm', 'rhumerus', 'rhand'),
    C('LeftLeg', 'lfemur', 'lfoot'),
    C('RightLeg', 'rfemur', 'rfoot'),
    C('LeftToe', 'ltoes', 'ltoes'),
    C('RightToe', 'rtoes', 'rtoes'),
  ],
});

export const CASCADEUR_PRESET = new RigPreset({
  name: 'Cascadeur',
  id: 'cascadeur',
  description: 'Cascadeur physics-based animation tool',
  caseSensitive: false,
  confidenceBoost: 0.12,
  signatureBones: [
    'root', 'hips',
    'spine', 'spine1', 'spine2', 'chest',
    'neck', 'head',
    'leftShoulder', 'leftArm', 'leftForeArm', 'leftHand',
    'rightShoulder', 'rightArm', 'rightForeArm', 'rightHand',
    'leftUpLeg', 'leftLeg', 'leftFoot',
    'rightUpLeg', 'rightLeg', 'rightFoot',
  ],
  optionalBones: [
    'leftToeBase', 'rightToeBase',
    'leftFootMarker', 'rightFootMarker',
    'leftHandMarker', 'rightHandMarker',
    'leftThumb1', 'leftThumb2', 'leftThumb3',
    'leftIndex1', 'leftIndex2', 'leftIndex3',
    'rightThumb1', 'rightThumb2', 'rightThumb3',
    'rightIndex1', 'rightIndex2', 'rightIndex3',
  ],
  chains: [
    C('Root', 'root', 'root'),
    C('Spine', 'spine', 'chest'),
    C('Neck', 'neck', 'neck'),
    C('Head', 'head', 'head'),
    C('LeftClavicle', 'leftShoulder', 'leftShoulder'),
    C('RightClavicle', 'rightShoulder', 'rightShoulder'),
    C('LeftArm', 'leftArm', 'leftHand'),
    C('RightArm', 'rightArm', 'rightHand'),
    C('LeftLeg', 'leftUpLeg', 'leftFoot'),
    C('RightLeg', 'rightUpLeg', 'rightFoot'),
    C('LeftToe', 'leftToeBase', 'leftToeBase'),
    C('RightToe', 'rightToeBase', 'rightToeBase'),
  ],
});

export const AUTORIG_PRO_PRESET = new RigPreset({
  name: 'Auto-Rig Pro',
  id: 'autorig_pro',
  description: 'Auto-Rig Pro Blender addon',
  caseSensitive: true,
  confidenceBoost: 0.15,
  signatureBones: [
    'c_root.x', 'c_root_master.x',
    'c_spine_01.x', 'c_spine_02.x', 'c_spine_03.x',
    'c_neck.x', 'c_head.x',
    'c_shoulder.l', 'c_arm_fk.l', 'c_forearm_fk.l', 'c_hand_fk.l',
    'c_shoulder.r', 'c_arm_fk.r', 'c_forearm_fk.r', 'c_hand_fk.r',
    'c_thigh_fk.l', 'c_leg_fk.l', 'c_foot_fk.l',
    'c_thigh_fk.r', 'c_leg_fk.r', 'c_foot_fk.r',
  ],
  optionalBones: [
    'c_arm_ik.l', 'c_arm_ik.r', 'c_leg_ik.l', 'c_leg_ik.r',
    'c_foot_ik.l', 'c_foot_ik.r', 'c_hand_ik.l', 'c_hand_ik.r',
    'c_toes_fk.l', 'c_toes_fk.r',
    'c_arm_twist.l', 'c_forearm_twist.l', 'c_arm_twist.r', 'c_forearm_twist.r',
    'c_thigh_twist.l', 'c_leg_twist.l', 'c_thigh_twist.r', 'c_leg_twist.r',
    'c_thumb1.l', 'c_thumb2.l', 'c_thumb3.l',
    'c_index1.l', 'c_index2.l', 'c_index3.l',
    'c_middle1.l', 'c_middle2.l', 'c_middle3.l',
    'c_ring1.l', 'c_ring2.l', 'c_ring3.l',
    'c_pinky1.l', 'c_pinky2.l', 'c_pinky3.l',
    'c_thumb1.r', 'c_thumb2.r', 'c_thumb3.r',
    'c_index1.r', 'c_index2.r', 'c_index3.r',
    'c_middle1.r', 'c_middle2.r', 'c_middle3.r',
    'c_ring1.r', 'c_ring2.r', 'c_ring3.r',
    'c_pinky1.r', 'c_pinky2.r', 'c_pinky3.r',
  ],
  chains: [
    C('Root', 'c_root.x', 'c_root.x'),
    C('Spine', 'c_spine_01.x', 'c_spine_03.x'),
    C('Neck', 'c_neck.x', 'c_neck.x'),
    C('Head', 'c_head.x', 'c_head.x'),
    C('LeftClavicle', 'c_shoulder.l', 'c_shoulder.l'),
    C('RightClavicle', 'c_shoulder.r', 'c_shoulder.r'),
    C('LeftArm', 'c_arm_fk.l', 'c_hand_fk.l'),
    C('RightArm', 'c_arm_fk.r', 'c_hand_fk.r'),
    C('LeftLeg', 'c_thigh_fk.l', 'c_foot_fk.l'),
    C('RightLeg', 'c_thigh_fk.r', 'c_foot_fk.r'),
    C('LeftToe', 'c_toes_fk.l', 'c_toes_fk.l'),
    C('RightToe', 'c_toes_fk.r', 'c_toes_fk.r'),
    C('LeftThumb', 'c_thumb1.l', 'c_thumb3.l'),
    C('LeftIndex', 'c_index1.l', 'c_index3.l'),
    C('LeftMiddle', 'c_middle1.l', 'c_middle3.l'),
    C('LeftRing', 'c_ring1.l', 'c_ring3.l'),
    C('LeftPinky', 'c_pinky1.l', 'c_pinky3.l'),
    C('RightThumb', 'c_thumb1.r', 'c_thumb3.r'),
    C('RightIndex', 'c_index1.r', 'c_index3.r'),
    C('RightMiddle', 'c_middle1.r', 'c_middle3.r'),
    C('RightRing', 'c_ring1.r', 'c_ring3.r'),
    C('RightPinky', 'c_pinky1.r', 'c_pinky3.r'),
  ],
});

export const ADVANCED_SKELETON_PRESET = new RigPreset({
  name: 'Advanced Skeleton',
  id: 'advanced_skeleton',
  description: 'Advanced Skeleton (Maya auto-rigger)',
  caseSensitive: true,
  confidenceBoost: 0.15,
  signatureBones: [
    'Main', 'Root_M',
    'Spine1_M', 'Spine2_M', 'Chest_M',
    'Neck_M', 'Head_M',
    'Scapula_L', 'Shoulder_L', 'Elbow_L', 'Wrist_L',
    'Scapula_R', 'Shoulder_R', 'Elbow_R', 'Wrist_R',
    'Hip_L', 'Knee_L', 'Ankle_L',
    'Hip_R', 'Knee_R', 'Ankle_R',
  ],
  optionalBones: [
    'Neck1_M', 'Neck2_M',
    'Toes_L', 'Toes_R', 'Toes1_L', 'Toes1_R', 'Toes2_L', 'Toes2_R',
    'ShoulderPart1_L', 'ShoulderPart2_L', 'ElbowPart1_L', 'ElbowPart2_L',
    'ShoulderPart1_R', 'ShoulderPart2_R', 'ElbowPart1_R', 'ElbowPart2_R',
    'HipPart1_L', 'HipPart2_L', 'HipPart1_R', 'HipPart2_R',
    'ThumbFinger1_L', 'ThumbFinger2_L', 'ThumbFinger3_L',
    'IndexFinger1_L', 'IndexFinger2_L', 'IndexFinger3_L',
    'MiddleFinger1_L', 'MiddleFinger2_L', 'MiddleFinger3_L',
    'RingFinger1_L', 'RingFinger2_L', 'RingFinger3_L',
    'PinkyFinger1_L', 'PinkyFinger2_L', 'PinkyFinger3_L',
    'ThumbFinger1_R', 'ThumbFinger2_R', 'ThumbFinger3_R',
    'IndexFinger1_R', 'IndexFinger2_R', 'IndexFinger3_R',
    'MiddleFinger1_R', 'MiddleFinger2_R', 'MiddleFinger3_R',
    'RingFinger1_R', 'RingFinger2_R', 'RingFinger3_R',
    'PinkyFinger1_R', 'PinkyFinger2_R', 'PinkyFinger3_R',
  ],
  chains: [
    C('Root', 'Main', 'Main'),
    C('Pelvis', 'Root_M', 'Root_M'),
    C('Spine', 'Spine1_M', 'Chest_M'),
    C('Neck', 'Neck_M', 'Neck_M'),
    C('Neck', 'Neck1_M', 'Neck2_M'),
    C('Head', 'Head_M', 'Head_M'),
    C('LeftClavicle', 'Scapula_L', 'Scapula_L'),
    C('RightClavicle', 'Scapula_R', 'Scapula_R'),
    C('LeftArm', 'Shoulder_L', 'Wrist_L', 'Wrist_L_Goal'),
    C('RightArm', 'Shoulder_R', 'Wrist_R', 'Wrist_R_Goal'),
    C('LeftLeg', 'Hip_L', 'Ankle_L', 'Ankle_L_Goal'),
    C('RightLeg', 'Hip_R', 'Ankle_R', 'Ankle_R_Goal'),
    C('LeftToe', 'Toes_L', 'Toes_L'),
    C('RightToe', 'Toes_R', 'Toes_R'),
    C('LeftThumb', 'ThumbFinger1_L', 'ThumbFinger3_L'),
    C('LeftIndex', 'IndexFinger1_L', 'IndexFinger3_L'),
    C('LeftMiddle', 'MiddleFinger1_L', 'MiddleFinger3_L'),
    C('LeftRing', 'RingFinger1_L', 'RingFinger3_L'),
    C('LeftPinky', 'PinkyFinger1_L', 'PinkyFinger3_L'),
    C('RightThumb', 'ThumbFinger1_R', 'ThumbFinger3_R'),
    C('RightIndex', 'IndexFinger1_R', 'IndexFinger3_R'),
    C('RightMiddle', 'MiddleFinger1_R', 'MiddleFinger3_R'),
    C('RightRing', 'RingFinger1_R', 'RingFinger3_R'),
    C('RightPinky', 'PinkyFinger1_R', 'PinkyFinger3_R'),
  ],
});

export const GODOT_HUMANOID_PRESET = new RigPreset({
  name: 'Godot Humanoid',
  id: 'godot_humanoid',
  description: 'Godot Engine humanoid skeleton standard',
  caseSensitive: false,
  confidenceBoost: 0.12,
  signatureBones: [
    'Root', 'Hips',
    'Spine', 'Chest', 'UpperChest',
    'Neck', 'Head',
    'LeftShoulder', 'LeftUpperArm', 'LeftLowerArm', 'LeftHand',
    'RightShoulder', 'RightUpperArm', 'RightLowerArm', 'RightHand',
    'LeftUpperLeg', 'LeftLowerLeg', 'LeftFoot',
    'RightUpperLeg', 'RightLowerLeg', 'RightFoot',
  ],
  optionalBones: [
    'LeftToes', 'RightToes',
    'LeftThumbMetacarpal', 'LeftThumbProximal', 'LeftThumbDistal',
    'LeftIndexProximal', 'LeftIndexIntermediate', 'LeftIndexDistal',
    'LeftMiddleProximal', 'LeftMiddleIntermediate', 'LeftMiddleDistal',
    'LeftRingProximal', 'LeftRingIntermediate', 'LeftRingDistal',
    'LeftLittleProximal', 'LeftLittleIntermediate', 'LeftLittleDistal',
    'RightThumbMetacarpal', 'RightThumbProximal', 'RightThumbDistal',
    'RightIndexProximal', 'RightIndexIntermediate', 'RightIndexDistal',
    'RightMiddleProximal', 'RightMiddleIntermediate', 'RightMiddleDistal',
    'RightRingProximal', 'RightRingIntermediate', 'RightRingDistal',
    'RightLittleProximal', 'RightLittleIntermediate', 'RightLittleDistal',
  ],
  chains: [
    C('Root', 'Root', 'Root'),
    C('Spine', 'Spine', 'UpperChest'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'LeftShoulder', 'LeftShoulder'),
    C('RightClavicle', 'RightShoulder', 'RightShoulder'),
    C('LeftArm', 'LeftUpperArm', 'LeftHand'),
    C('RightArm', 'RightUpperArm', 'RightHand'),
    C('LeftLeg', 'LeftUpperLeg', 'LeftFoot'),
    C('RightLeg', 'RightUpperLeg', 'RightFoot'),
    C('LeftToe', 'LeftToes', 'LeftToes'),
    C('RightToe', 'RightToes', 'RightToes'),
  ],
});

export const OPTITRACK_PRESET = new RigPreset({
  name: 'OptiTrack',
  id: 'optitrack',
  description: 'OptiTrack optical motion capture system',
  caseSensitive: false,
  confidenceBoost: 0.12,
  signatureBones: [
    'Hip', 'Ab', 'Chest', 'Neck', 'Head',
    'LShoulder', 'LUArm', 'LFArm', 'LHand',
    'RShoulder', 'RUArm', 'RFArm', 'RHand',
    'LThigh', 'LShin', 'LFoot',
    'RThigh', 'RShin', 'RFoot',
  ],
  optionalBones: [
    'LToe', 'RToe',
    'LThumb1', 'LThumb2', 'LThumb3',
    'LIndex1', 'LIndex2', 'LIndex3',
    'LMiddle1', 'LMiddle2', 'LMiddle3',
    'LRing1', 'LRing2', 'LRing3',
    'LPinky1', 'LPinky2', 'LPinky3',
    'RThumb1', 'RThumb2', 'RThumb3',
    'RIndex1', 'RIndex2', 'RIndex3',
    'RMiddle1', 'RMiddle2', 'RMiddle3',
    'RRing1', 'RRing2', 'RRing3',
    'RPinky1', 'RPinky2', 'RPinky3',
  ],
  chains: [
    C('Root', 'Hip', 'Hip'),
    C('Spine', 'Ab', 'Chest'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'LShoulder', 'LShoulder'),
    C('RightClavicle', 'RShoulder', 'RShoulder'),
    C('LeftArm', 'LUArm', 'LHand'),
    C('RightArm', 'RUArm', 'RHand'),
    C('LeftLeg', 'LThigh', 'LFoot'),
    C('RightLeg', 'RThigh', 'RFoot'),
    C('LeftToe', 'LToe', 'LToe'),
    C('RightToe', 'RToe', 'RToe'),
  ],
});

export const PERCEPTION_NEURON_PRESET = new RigPreset({
  name: 'Perception Neuron',
  id: 'perception_neuron',
  description: 'Noitom Perception Neuron motion capture',
  caseSensitive: false,
  confidenceBoost: 0.12,
  signatureBones: [
    'Hips', 'Spine', 'Spine1', 'Spine2', 'Spine3', 'Neck', 'Head',
    'LeftShoulder', 'LeftArm', 'LeftForeArm', 'LeftHand',
    'RightShoulder', 'RightArm', 'RightForeArm', 'RightHand',
    'LeftUpLeg', 'LeftLeg', 'LeftFoot',
    'RightUpLeg', 'RightLeg', 'RightFoot',
  ],
  optionalBones: [
    'LeftToeBase', 'RightToeBase',
    'LeftHandThumb1', 'LeftHandThumb2', 'LeftHandThumb3',
    'LeftHandIndex1', 'LeftHandIndex2', 'LeftHandIndex3',
    'LeftHandMiddle1', 'LeftHandMiddle2', 'LeftHandMiddle3',
    'LeftHandRing1', 'LeftHandRing2', 'LeftHandRing3',
    'LeftHandPinky1', 'LeftHandPinky2', 'LeftHandPinky3',
    'RightHandThumb1', 'RightHandThumb2', 'RightHandThumb3',
    'RightHandIndex1', 'RightHandIndex2', 'RightHandIndex3',
    'RightHandMiddle1', 'RightHandMiddle2', 'RightHandMiddle3',
    'RightHandRing1', 'RightHandRing2', 'RightHandRing3',
    'RightHandPinky1', 'RightHandPinky2', 'RightHandPinky3',
  ],
  chains: [
    C('Spine', 'Spine', 'Spine3'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'LeftShoulder', 'LeftShoulder'),
    C('RightClavicle', 'RightShoulder', 'RightShoulder'),
    C('LeftArm', 'LeftArm', 'LeftHand'),
    C('RightArm', 'RightArm', 'RightHand'),
    C('LeftLeg', 'LeftUpLeg', 'LeftFoot'),
    C('RightLeg', 'RightUpLeg', 'RightFoot'),
    C('LeftToe', 'LeftToeBase', 'LeftToeBase'),
    C('RightToe', 'RightToeBase', 'RightToeBase'),
  ],
});

export const ROKOKO_PRESET = new RigPreset({
  name: 'Rokoko',
  id: 'rokoko',
  description: 'Rokoko motion capture suit skeleton',
  caseSensitive: true,
  confidenceBoost: 0.12,
  signatureBones: [
    'Hip', 'Spine', 'Spine1', 'Spine2', 'Neck', 'Head',
    'LeftShoulder', 'LeftArm', 'LeftForeArm', 'LeftHand',
    'RightShoulder', 'RightArm', 'RightForeArm', 'RightHand',
    'LeftUpLeg', 'LeftLeg', 'LeftFoot',
    'RightUpLeg', 'RightLeg', 'RightFoot',
  ],
  optionalBones: [
    'Chest', 'LeftToe', 'RightToe', 'LeftToeEnd', 'RightToeEnd',
    'LeftHandThumb1', 'LeftHandThumb2', 'LeftHandThumb3',
    'LeftHandIndex1', 'LeftHandIndex2', 'LeftHandIndex3',
    'LeftHandMiddle1', 'LeftHandMiddle2', 'LeftHandMiddle3',
    'LeftHandRing1', 'LeftHandRing2', 'LeftHandRing3',
    'LeftHandPinky1', 'LeftHandPinky2', 'LeftHandPinky3',
    'RightHandThumb1', 'RightHandThumb2', 'RightHandThumb3',
    'RightHandIndex1', 'RightHandIndex2', 'RightHandIndex3',
    'RightHandMiddle1', 'RightHandMiddle2', 'RightHandMiddle3',
    'RightHandRing1', 'RightHandRing2', 'RightHandRing3',
    'RightHandPinky1', 'RightHandPinky2', 'RightHandPinky3',
  ],
  chains: [
    C('Spine', 'Spine', 'Spine2'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'LeftShoulder', 'LeftShoulder'),
    C('RightClavicle', 'RightShoulder', 'RightShoulder'),
    C('LeftArm', 'LeftArm', 'LeftHand'),
    C('RightArm', 'RightArm', 'RightHand'),
    C('LeftLeg', 'LeftUpLeg', 'LeftFoot'),
    C('RightLeg', 'RightUpLeg', 'RightFoot'),
    C('LeftToe', 'LeftToe', 'LeftToe'),
    C('RightToe', 'RightToe', 'RightToe'),
    C('LeftThumb', 'LeftHandThumb1', 'LeftHandThumb3'),
    C('LeftIndex', 'LeftHandIndex1', 'LeftHandIndex3'),
    C('LeftMiddle', 'LeftHandMiddle1', 'LeftHandMiddle3'),
    C('LeftRing', 'LeftHandRing1', 'LeftHandRing3'),
    C('LeftPinky', 'LeftHandPinky1', 'LeftHandPinky3'),
    C('RightThumb', 'RightHandThumb1', 'RightHandThumb3'),
    C('RightIndex', 'RightHandIndex1', 'RightHandIndex3'),
    C('RightMiddle', 'RightHandMiddle1', 'RightHandMiddle3'),
    C('RightRing', 'RightHandRing1', 'RightHandRing3'),
    C('RightPinky', 'RightHandPinky1', 'RightHandPinky3'),
  ],
});

export const TRUEBONES_PRESET = new RigPreset({
  name: 'Truebones',
  id: 'truebones',
  description: 'Truebones motion capture and character assets',
  caseSensitive: false,
  confidenceBoost: 0.10,
  signatureBones: [
    'Hips', 'Spine', 'Spine1', 'Spine2', 'Neck', 'Head',
    'LeftShoulder', 'LeftArm', 'LeftForeArm', 'LeftHand',
    'RightShoulder', 'RightArm', 'RightForeArm', 'RightHand',
    'LeftUpLeg', 'LeftLeg', 'LeftFoot',
    'RightUpLeg', 'RightLeg', 'RightFoot',
  ],
  optionalBones: [
    'LeftToeBase', 'RightToeBase',
    'LeftHandThumb1', 'LeftHandThumb2',
    'LeftHandIndex1', 'LeftHandIndex2',
    'RightHandThumb1', 'RightHandThumb2',
    'RightHandIndex1', 'RightHandIndex2',
  ],
  chains: [
    C('Pelvis', 'Hips', 'Hips'),
    C('Spine', 'Spine', 'Spine2'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'LeftShoulder', 'LeftShoulder'),
    C('RightClavicle', 'RightShoulder', 'RightShoulder'),
    C('LeftArm', 'LeftArm', 'LeftHand'),
    C('RightArm', 'RightArm', 'RightHand'),
    C('LeftLeg', 'LeftUpLeg', 'LeftFoot'),
    C('RightLeg', 'RightUpLeg', 'RightFoot'),
    C('LeftToe', 'LeftToeBase', 'LeftToeBase'),
    C('RightToe', 'RightToeBase', 'RightToeBase'),
  ],
});

export const VRM_VROID_PRESET = new RigPreset({
  name: 'VRM/VRoid',
  id: 'vrm_vroid',
  description: 'VRM humanoid avatar format (VRoid Studio, VRChat)',
  caseSensitive: true,
  confidenceBoost: 0.15,
  signatureBones: [
    'J_Bip_C_Hips',
    'J_Bip_C_Spine', 'J_Bip_C_Chest', 'J_Bip_C_UpperChest',
    'J_Bip_C_Neck', 'J_Bip_C_Head',
    'J_Bip_L_Shoulder', 'J_Bip_L_UpperArm', 'J_Bip_L_LowerArm', 'J_Bip_L_Hand',
    'J_Bip_R_Shoulder', 'J_Bip_R_UpperArm', 'J_Bip_R_LowerArm', 'J_Bip_R_Hand',
    'J_Bip_L_UpperLeg', 'J_Bip_L_LowerLeg', 'J_Bip_L_Foot',
    'J_Bip_R_UpperLeg', 'J_Bip_R_LowerLeg', 'J_Bip_R_Foot',
  ],
  optionalBones: [
    'J_Bip_L_ToeBase', 'J_Bip_R_ToeBase',
    'J_Adj_L_FaceEye', 'J_Adj_R_FaceEye',
    'J_Bip_L_Thumb1', 'J_Bip_L_Thumb2', 'J_Bip_L_Thumb3',
    'J_Bip_L_Index1', 'J_Bip_L_Index2', 'J_Bip_L_Index3',
    'J_Bip_L_Middle1', 'J_Bip_L_Middle2', 'J_Bip_L_Middle3',
    'J_Bip_L_Ring1', 'J_Bip_L_Ring2', 'J_Bip_L_Ring3',
    'J_Bip_L_Little1', 'J_Bip_L_Little2', 'J_Bip_L_Little3',
    'J_Bip_R_Thumb1', 'J_Bip_R_Thumb2', 'J_Bip_R_Thumb3',
    'J_Bip_R_Index1', 'J_Bip_R_Index2', 'J_Bip_R_Index3',
    'J_Bip_R_Middle1', 'J_Bip_R_Middle2', 'J_Bip_R_Middle3',
    'J_Bip_R_Ring1', 'J_Bip_R_Ring2', 'J_Bip_R_Ring3',
    'J_Bip_R_Little1', 'J_Bip_R_Little2', 'J_Bip_R_Little3',
    'J_Sec_L_Bust1', 'J_Sec_R_Bust1',
  ],
  chains: [
    C('Spine', 'J_Bip_C_Spine', 'J_Bip_C_UpperChest'),
    C('Neck', 'J_Bip_C_Neck', 'J_Bip_C_Neck'),
    C('Head', 'J_Bip_C_Head', 'J_Bip_C_Head'),
    C('LeftClavicle', 'J_Bip_L_Shoulder', 'J_Bip_L_Shoulder'),
    C('RightClavicle', 'J_Bip_R_Shoulder', 'J_Bip_R_Shoulder'),
    C('LeftArm', 'J_Bip_L_UpperArm', 'J_Bip_L_Hand'),
    C('RightArm', 'J_Bip_R_UpperArm', 'J_Bip_R_Hand'),
    C('LeftLeg', 'J_Bip_L_UpperLeg', 'J_Bip_L_Foot'),
    C('RightLeg', 'J_Bip_R_UpperLeg', 'J_Bip_R_Foot'),
    C('LeftToe', 'J_Bip_L_ToeBase', 'J_Bip_L_ToeBase'),
    C('RightToe', 'J_Bip_R_ToeBase', 'J_Bip_R_ToeBase'),
    C('LeftThumb', 'J_Bip_L_Thumb1', 'J_Bip_L_Thumb3'),
    C('LeftIndex', 'J_Bip_L_Index1', 'J_Bip_L_Index3'),
    C('LeftMiddle', 'J_Bip_L_Middle1', 'J_Bip_L_Middle3'),
    C('LeftRing', 'J_Bip_L_Ring1', 'J_Bip_L_Ring3'),
    C('LeftPinky', 'J_Bip_L_Little1', 'J_Bip_L_Little3'),
    C('RightThumb', 'J_Bip_R_Thumb1', 'J_Bip_R_Thumb3'),
    C('RightIndex', 'J_Bip_R_Index1', 'J_Bip_R_Index3'),
    C('RightMiddle', 'J_Bip_R_Middle1', 'J_Bip_R_Middle3'),
    C('RightRing', 'J_Bip_R_Ring1', 'J_Bip_R_Ring3'),
    C('RightPinky', 'J_Bip_R_Little1', 'J_Bip_R_Little3'),
  ],
});

export const XSENS_MVN_PRESET = new RigPreset({
  name: 'Xsens MVN',
  id: 'xsens_mvn',
  description: 'Xsens MVN professional motion capture',
  caseSensitive: true,
  confidenceBoost: 0.12,
  signatureBones: [
    'Pelvis', 'L5', 'L3', 'T12', 'T8', 'Neck', 'Head',
    'L_Shoulder', 'L_UpperArm', 'L_Forearm', 'L_Hand',
    'R_Shoulder', 'R_UpperArm', 'R_Forearm', 'R_Hand',
    'L_UpperLeg', 'L_LowerLeg', 'L_Foot',
    'R_UpperLeg', 'R_LowerLeg', 'R_Foot',
  ],
  optionalBones: [
    'L_Toe', 'R_Toe',
    'L_Thumb_Proximal', 'L_Thumb_Distal',
    'L_Index_Proximal', 'L_Index_Intermediate', 'L_Index_Distal',
    'L_Middle_Proximal', 'L_Middle_Intermediate', 'L_Middle_Distal',
    'L_Ring_Proximal', 'L_Ring_Intermediate', 'L_Ring_Distal',
    'L_Pinky_Proximal', 'L_Pinky_Intermediate', 'L_Pinky_Distal',
    'R_Thumb_Proximal', 'R_Thumb_Distal',
    'R_Index_Proximal', 'R_Index_Intermediate', 'R_Index_Distal',
    'R_Middle_Proximal', 'R_Middle_Intermediate', 'R_Middle_Distal',
    'R_Ring_Proximal', 'R_Ring_Intermediate', 'R_Ring_Distal',
    'R_Pinky_Proximal', 'R_Pinky_Intermediate', 'R_Pinky_Distal',
  ],
  chains: [
    C('Root', 'Pelvis', 'Pelvis'),
    C('Spine', 'L5', 'T8'),
    C('Neck', 'Neck', 'Neck'),
    C('Head', 'Head', 'Head'),
    C('LeftClavicle', 'L_Shoulder', 'L_Shoulder'),
    C('RightClavicle', 'R_Shoulder', 'R_Shoulder'),
    C('LeftArm', 'L_UpperArm', 'L_Hand'),
    C('RightArm', 'R_UpperArm', 'R_Hand'),
    C('LeftLeg', 'L_UpperLeg', 'L_Foot'),
    C('RightLeg', 'R_UpperLeg', 'R_Foot'),
    C('LeftToe', 'L_Toe', 'L_Toe'),
    C('RightToe', 'R_Toe', 'R_Toe'),
  ],
});

// ═══════════════════════════════════════════════════════════════════════
// REGISTRY
// ═══════════════════════════════════════════════════════════════════════

export const ALL_PRESETS: RigPreset[] = [
  MIXAMO_PRESET,
  UE5_MANNEQUIN_PRESET,
  UE4_MANNEQUIN_PRESET,
  BVH_STANDARD_PRESET,
  RIGIFY_PRESET,
  MAYA_HUMANIK_PRESET,
  MAX_BIPED_PRESET,
  MAX_CAT_PRESET,
  UNITY_HUMANOID_PRESET,
  MOTIONBUILDER_PRESET,
  ICLONE_CC_PRESET,
  DAZ_GENESIS_PRESET,
  CINEMA4D_PRESET,
  CMU_MOCAP_PRESET,
  CASCADEUR_PRESET,
  AUTORIG_PRO_PRESET,
  ADVANCED_SKELETON_PRESET,
  GODOT_HUMANOID_PRESET,
  OPTITRACK_PRESET,
  PERCEPTION_NEURON_PRESET,
  ROKOKO_PRESET,
  TRUEBONES_PRESET,
  VRM_VROID_PRESET,
  XSENS_MVN_PRESET,
];

export const PRESET_REGISTRY: Record<string, RigPreset> = {};
for (const preset of ALL_PRESETS) {
  PRESET_REGISTRY[preset.id] = preset;
}
